'use client';

import { useState } from 'react';
import SaveCalculationModal from '@/components/common/SaveCalculationModal';
import { useAuth } from '@/contexts/AuthContext';

interface MortgageState {
  homePrice: number;
  downPayment: number;
  interestRate: number;
  loanTerm: number;
}

const INITIAL_STATE: MortgageState = {
  homePrice: 350000,
  downPayment: 20,
  interestRate: 6.5,
  loanTerm: 30,
};

export default function CalculatorMortgage() {
  const [inputs, setInputs] = useState<MortgageState>(INITIAL_STATE);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const { user } = useAuth();

  const calculate = () => {
    const downPaymentAmount = inputs.homePrice * (inputs.downPayment / 100);
    const loanAmount = inputs.homePrice - downPaymentAmount;
    const monthlyRate = inputs.interestRate / 100 / 12;
    const months = inputs.loanTerm * 12;
    const monthlyPayment =
      loanAmount *
      (monthlyRate * Math.pow(1 + monthlyRate, months)) /
      (Math.pow(1 + monthlyRate, months) - 1);
    const totalPaid = monthlyPayment * months;
    const totalInterest = totalPaid - loanAmount;

    return {
      monthlyPayment,
      downPaymentAmount,
      loanAmount,
      totalPaid,
      totalInterest,
    };
  };

  const results = calculate();

  const handleReset = () => {
    setInputs(INITIAL_STATE);
  };

  return (
    <div className="border border-gray-300 bg-gray-50 p-4">
      {/* Inputs */}
      <div className="mb-6">
        <h2 className="text-lg font-bold mb-3">Inputs</h2>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-bold mb-1">Home Price ($)</label>
            <input
              type="number"
              value={inputs.homePrice}
              onChange={(e) => setInputs({ ...inputs, homePrice: Number(e.target.value) })}
              className="w-full border border-gray-400 px-2 py-1 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-bold mb-1">Down Payment (%)</label>
            <input
              type="number"
              value={inputs.downPayment}
              onChange={(e) => setInputs({ ...inputs, downPayment: Number(e.target.value) })}
              className="w-full border border-gray-400 px-2 py-1 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-bold mb-1">Interest Rate (%)</label>
            <input
              type="number"
              step="0.1"
              value={inputs.interestRate}
              onChange={(e) => setInputs({ ...inputs, interestRate: Number(e.target.value) })}
              className="w-full border border-gray-400 px-2 py-1 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-bold mb-1">Loan Term (years)</label>
            <input
              type="number"
              value={inputs.loanTerm}
              onChange={(e) => setInputs({ ...inputs, loanTerm: Number(e.target.value) })}
              className="w-full border border-gray-400 px-2 py-1 text-sm"
            />
          </div>
        </div>
        <button
          onClick={handleReset}
          className="mt-3 text-sm text-blue-600 hover:underline"
        >
          reset
        </button>
      </div>

      {/* Results */}
      <div className="border-t border-gray-300 pt-4">
        <h2 className="text-lg font-bold mb-3">Results</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>Monthly Payment:</span>
            <span className="font-bold">${results.monthlyPayment.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span>Down Payment:</span>
            <span>${results.downPaymentAmount.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span>Loan Amount:</span>
            <span>${results.loanAmount.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span>Total Paid:</span>
            <span>${results.totalPaid.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span>Total Interest:</span>
            <span>${results.totalInterest.toLocaleString()}</span>
          </div>
        </div>
        {user && (
          <button
            onClick={() => setShowSaveModal(true)}
            className="mt-3 text-sm text-blue-600 hover:underline"
          >
            save calculation
          </button>
        )}
      </div>

      {showSaveModal && (
        <SaveCalculationModal
          calculatorType="mortgage"
          inputs={inputs}
          results={results}
          onClose={() => setShowSaveModal(false)}
        />
      )}
    </div>
  );
}